import { explicitMaybe, stringLiteral } from '@execonline-inc/decoders';

import { Decoder, array, boolean, field, number, oneOf, string, succeed } from 'jsonous';
import {
  VideoPlaybackQuality,
  VideoPlaybackSource,
  VideoPlaybackSourceLabel,
  VideoPlaybackType,
} from '../JWPlayer/Types';
import { resourceDecoder } from '../Resource/Decoders';
import {
  HighProgressiveStream,
  HighProgressiveStreamResource,
  LowProgressiveStream,
  LowProgressiveStreamResource,
  MediumProgressiveStream,
  MediumProgressiveStreamResource,
  PreferredVideoPlayback,
  ReqHlsVideoAsset,
  ReqHlsVideoAssetResource,
  Subtitle,
  SubtitleKind,
  SubtitleResource,
  VideoPlaybackSubtitles,
} from './Types';

const videoPlaybackTypeDecoder: Decoder<VideoPlaybackType> = oneOf([
  stringLiteral<VideoPlaybackType>('progressive'),
  stringLiteral<VideoPlaybackType>('adaptive'),
]);

const videoPlaybackQualityDecoder: Decoder<VideoPlaybackQuality> = oneOf([
  stringLiteral<VideoPlaybackQuality>('low'),
  stringLiteral<VideoPlaybackQuality>('medium'),
  stringLiteral<VideoPlaybackQuality>('high'),
]);

export const preferredVideoPlaybackDecoder: Decoder<PreferredVideoPlayback> = succeed({})
  .assign('type', field('type', videoPlaybackTypeDecoder))
  .assign('qualities', field('qualities', array(videoPlaybackQualityDecoder)));

const videoPlaybackSourceLabelDecoder: Decoder<VideoPlaybackSourceLabel> = oneOf([
  stringLiteral<VideoPlaybackSourceLabel>('Low'),
  stringLiteral<VideoPlaybackSourceLabel>('Medium'),
  stringLiteral<VideoPlaybackSourceLabel>('High'),
]);

export const videoPlaybackSourceDecoder: Decoder<VideoPlaybackSource> = succeed({})
  .assign('file', field('file', string))
  .assign('label', field('label', videoPlaybackSourceLabelDecoder))
  .assign('default', field('default', boolean));

export const subtitleKindDecoder: Decoder<SubtitleKind> = oneOf<SubtitleKind>([
  stringLiteral<SubtitleKind>('auto-load-on'),
  stringLiteral<SubtitleKind>('auto-load-off'),
]);

export const videoPlaybackSubtitlesDecoder: Decoder<VideoPlaybackSubtitles> = succeed({})
  .assign('file', field('file', string))
  .assign('label', field('label', string))
  .assign('kind', field('kind', subtitleKindDecoder));

export const highProgressiveStreamDecoder: Decoder<HighProgressiveStream> = succeed({})
  .assign('label', field('label', stringLiteral('High')))
  .assign('default', field('default', boolean));

export const highProgressiveStreamResourceDecoder: Decoder<HighProgressiveStreamResource> =
  resourceDecoder(highProgressiveStreamDecoder);

export const mediumProgressiveStreamDecoder: Decoder<MediumProgressiveStream> = succeed({})
  .assign('label', field('label', stringLiteral('Medium')))
  .assign('default', field('default', boolean));

export const mediumProgressiveStreamResourceDecoder: Decoder<MediumProgressiveStreamResource> =
  resourceDecoder(mediumProgressiveStreamDecoder);

export const lowProgressiveStreamDecoder: Decoder<LowProgressiveStream> = succeed({})
  .assign('label', field('label', stringLiteral('Low')))
  .assign('default', field('default', boolean));

export const lowProgressiveStreamResourceDecoder: Decoder<LowProgressiveStreamResource> =
  resourceDecoder(lowProgressiveStreamDecoder);

export const subtitleDecoder: Decoder<Subtitle> = succeed({})
  .assign('label', field('label', string))
  .assign('kind', field('kind', subtitleKindDecoder));

export const subtitleResourceDecoder: Decoder<SubtitleResource> = resourceDecoder(subtitleDecoder);

const reqHlsVideoAssetDecoder: Decoder<ReqHlsVideoAsset> = succeed({})
  .assign('uuid', field('uuid', string))
  .assign('title', field('title', explicitMaybe(string)))
  .assign('id', field('id', number))
  .assign('mediaId', field('media_id', explicitMaybe(string)))
  .assign(
    'preferredVideoPlayback',
    field('preferred_video_playback', preferredVideoPlaybackDecoder),
  )
  .assign(
    'highProgressiveStream',
    field('high_progressive_stream', highProgressiveStreamResourceDecoder),
  )
  .assign(
    'mediumProgressiveStream',
    field('medium_progressive_stream', mediumProgressiveStreamResourceDecoder),
  )
  .assign(
    'lowProgressiveStream',
    field('low_progressive_stream', lowProgressiveStreamResourceDecoder),
  )
  .assign('subtitles', field('subtitles', array(subtitleResourceDecoder)));

export const reqHlsVideoAssetResourceDecoder: Decoder<ReqHlsVideoAssetResource> =
  resourceDecoder(reqHlsVideoAssetDecoder);
